import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  CheckCircle, Star, Zap, Crown, Loader2, ArrowRight, Gift, Sparkles, Users, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [foundingSpots, setFoundingSpots] = useState(null);
  const [promoStatus, setPromoStatus] = useState(null);

  useEffect(() => {
    loadPlans();
    loadFoundingSpots();
    loadPromoStatus();
    checkPaymentStatus();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await subscriptionAPI.getPlans();
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const loadFoundingSpots = async () => {
    try {
      const response = await api.get('/founding-member/availability');
      setFoundingSpots(response.data);
    } catch (error) {
      console.error('Failed to load founding spots');
    }
  };

  const loadPromoStatus = async () => {
    try {
      const response = await api.get('/subscription/promo-status');
      setPromoStatus(response.data);
    } catch (error) {
      console.error('Failed to load promo status');
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && isAuthenticated) {
      setCheckingPayment(true);
      try {
        // Poll for status
        for (let i = 0; i < 5; i++) {
          const status = await subscriptionAPI.getCheckoutStatus(sessionId);
          if (status.data.payment_status === 'paid') {
            toast.success('Subscription activated! Welcome to Professional.');
            navigate('/dashboard');
            return;
          }
          await new Promise(r => setTimeout(r, 2000));
        }
        toast.info('Payment is being processed. Check your dashboard for status.');
      } catch (error) {
        toast.error('Error checking payment status');
      } finally {
        setCheckingPayment(false);
      }
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // If promo is active, activate free subscription
    if (promoStatus?.promo_active) {
      setSubscribing(planId);
      try {
        const response = await api.post(`/subscription/activate-free?plan_id=${planId}`);
        toast.success(response.data.message);
        
        // If they need verification, redirect to profile to complete it
        if (response.data.requires_verification) {
          toast.info('Complete your profile verification to unlock full access!', { duration: 5000 });
          navigate('/profile?tab=verification');
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to activate subscription');
      } finally {
        setSubscribing(null);
      }
      return;
    }

    // Normal paid checkout
    setSubscribing(planId);
    try {
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.createCheckout(planId, originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
      setSubscribing(null);
    }
  };

  const planIcons = {
    basic: Star,
    professional: Zap,
    elite: Crown,
  };

  const planColors = {
    basic: 'border-border',
    professional: 'border-primary bg-primary/5',
    elite: 'border-[#4ADE80]/50 bg-[#4ADE80]/5',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Processing Payment...</h2>
          <p className="text-muted-foreground">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold uppercase font-['Barlow_Condensed'] tracking-tight mb-4">
            Simple, Transparent <span className="text-primary">Pricing</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Start free and upgrade when you're ready. All plans include access to our verified network.
          </p>
        </div>

        {/* LAUNCH PROMO BANNER - Free for first subscribers */}
        {promoStatus?.promo_active && (
          <div className="mb-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 border-2 border-yellow-500/50 animate-fade-in relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIvPjwvZz48L3N2Zz4=')] opacity-50"></div>
            <div className="relative flex flex-col lg:flex-row items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30 animate-pulse">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <div className="text-center lg:text-left flex-1">
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                  <span className="px-3 py-1 rounded-full bg-yellow-500/30 text-yellow-300 text-xs font-bold uppercase tracking-wider">
                    Limited Time
                  </span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  {promoStatus.promo_name}: ALL PLANS FREE!
                </h3>
                <p className="text-lg text-gray-300 mb-4">
                  Be one of our first <span className="text-yellow-400 font-bold">{promoStatus.free_until}</span> members and get full access FREE for <span className="text-yellow-400 font-bold">{promoStatus.free_months} months</span>!
                </p>
                
                {/* Progress bar */}
                <div className="max-w-md mx-auto lg:mx-0">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {promoStatus.free_until - promoStatus.spots_remaining} joined
                    </span>
                    <span className="text-yellow-400 font-bold">
                      {promoStatus.spots_remaining} spots left!
                    </span>
                  </div>
                  <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-full transition-all duration-1000"
                      style={{ width: `${promoStatus.percent_filled}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-orange-300">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Limited spots available</span>
              </div>
            </div>
          </div>
        )}

        {/* Founding Member Banner - Only show if promo is not active */}
        {!promoStatus?.promo_active && foundingSpots && foundingSpots.spots_remaining > 0 && (
          <div className="mb-8 p-4 sm:p-6 rounded-xl bg-gradient-to-r from-[#4ADE80]/10 via-[#86EFAC]/10 to-[#16A34A]/10 border border-[#4ADE80]/30 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#4ADE80]/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6 text-[#4ADE80]" />
              </div>
              <div className="text-center sm:text-left flex-1">
                <h3 className="text-lg font-bold text-white mb-1">
                  Founding Member Program - Only {foundingSpots.spots_remaining} Spots Left!
                </h3>
                <p className="text-sm text-gray-400">
                  Be one of the first 300 members and lock in discounted rates forever.
                </p>
                <div className="flex flex-wrap gap-4 mt-2 justify-center sm:justify-start text-xs">
                  <span className="text-[#86EFAC]">
                    <Crown className="w-3 h-3 inline mr-1" />
                    First 100: <strong>Lifetime</strong> rate lock ({foundingSpots.lifetime?.remaining || 0} left)
                  </span>
                  <span className="text-[#4ADE80]">
                    Next 100: <strong>3-Year</strong> rate lock ({foundingSpots.three_year?.remaining || 0} left)
                  </span>
                  <span className="text-[#B8860B]">
                    Next 100: <strong>1-Year</strong> rate lock ({foundingSpots.one_year?.remaining || 0} left)
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const Icon = planIcons[plan.id] || Star;
            const colorClass = planColors[plan.id] || 'border-border';
            const isProfessional = plan.id === 'professional';

            return (
              <Card
                key={plan.id}
                className={`relative ${colorClass} bg-card/50 animate-fade-in card-hover`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`plan-${plan.id}`}
              >
                {isProfessional && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-semibold uppercase">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <div className={`w-14 h-14 rounded-sm mx-auto mb-4 flex items-center justify-center ${
                    isProfessional ? 'bg-primary' : 'bg-secondary'
                  }`}>
                    <Icon className={`w-7 h-7 ${isProfessional ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                  </div>
                  <CardTitle className="text-2xl font-['Barlow_Condensed'] uppercase">
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    {promoStatus?.promo_active ? (
                      <div>
                        <span className="text-4xl font-bold font-['Barlow_Condensed'] text-green-400">
                          FREE
                        </span>
                        <span className="block text-sm text-muted-foreground line-through mt-1">
                          Was ${plan.price}/month
                        </span>
                        <span className="block text-xs text-yellow-400 mt-1">
                          For {promoStatus.free_months} months!
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className="text-4xl font-bold font-['Barlow_Condensed']">
                          ${plan.price}
                        </span>
                        {plan.price > 0 && (
                          <span className="text-muted-foreground">/month</span>
                        )}
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full h-12 uppercase tracking-wider font-semibold ${
                      promoStatus?.promo_active 
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black' 
                        : isProfessional ? 'glow-gold' : ''
                    }`}
                    variant={isProfessional ? 'default' : 'outline'}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {subscribing === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : promoStatus?.promo_active ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Get Free Access
                      </>
                    ) : (
                      <>
                        Subscribe
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ / Info */}
        <div className="mt-16 text-center animate-fade-in delay-300">
          <h2 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase mb-4">
            Questions?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            All plans include our verification system and access to the Backlot community.
            Professional users get priority verification, unlimited load posting, and premium features.
            Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
